/**
 * Theme Context & Provider
 *
 * Manages app-wide theme state. Reads initial value synchronously from MMKV
 * to avoid a flash of the wrong theme on startup. Persists preference to MMKV.
 */
import React, { createContext, useCallback, useMemo, useState } from 'react';
import { Appearance } from 'react-native';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { getTheme } from '@/constants/shared/theme';
import { logger } from '@/utils/common/logger';
import { useWebCanvasColor } from '@/hooks/shared/useWebCanvasColor';
import type {
  ThemeMode,
  RedesignThemeColors,
  SemanticThemeColors,
  ComponentThemeColors,
} from '@/constants/shared/theme';

const THEME_STORAGE_KEY = 'theme-mode';

export interface ThemeContextValue {
  mode: ThemeMode;
  colors: RedesignThemeColors;
  semantic: SemanticThemeColors;
  components: ComponentThemeColors;
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

function readStoredTheme(): ThemeMode {
  try {
    const stored = SecureStorage.getInstance().getItem<ThemeMode>(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // MMKV unavailable — fall back to OS preference or default
  }
  // No stored preference — respect OS setting on first launch
  const osScheme = Appearance.getColorScheme();
  return osScheme === 'light' ? 'light' : 'dark';
}

// Initialise once at module level for synchronous first render
const initialMode = readStoredTheme();
const initialTheme = getTheme(initialMode);

const defaultValue: ThemeContextValue = {
  mode: initialMode,
  colors: initialTheme.redesign,
  semantic: initialTheme.semantic,
  components: initialTheme.components,
  setTheme: () => {},
  toggleTheme: () => {},
};

export const ThemeContext = createContext<ThemeContextValue>(defaultValue);

export const ThemeProvider: React.FC<{
  children: React.ReactNode;
  /**
   * Overrides the mode this provider starts in. The app never passes it —
   * `initialMode` (stored preference, else OS) is what ships. It exists so
   * tests can mount a tree in light mode: `initialMode` is resolved once at
   * module load, so without this a test can only ever assert against whichever
   * mode the environment happened to pick, which in practice is always dark.
   * That is how a token can be wrong in light and still pass every test.
   */
  initialMode?: ThemeMode;
}> = ({ children, initialMode: initialModeOverride }) => {
  const [mode, setMode] = useState<ThemeMode>(initialModeOverride ?? initialMode);

  const setTheme = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
    try {
      SecureStorage.getInstance().setItem(THEME_STORAGE_KEY, newMode);
    } catch {
      // MMKV write failure — theme still changes in-memory
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setMode((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      try {
        SecureStorage.getInstance().setItem(THEME_STORAGE_KEY, next);
      } catch (e) {
        logger.warn('themeTogglePersistFailed', e);
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(() => {
    const theme = getTheme(mode);
    return {
      mode,
      colors: theme.redesign,
      semantic: theme.semantic,
      components: theme.components,
      setTheme,
      toggleTheme,
    };
  }, [mode, setTheme, toggleTheme]);

  // On web the page canvas — what Safari paints behind its status bar and
  // toolbar — follows the theme too; the template only guesses from the OS scheme.
  useWebCanvasColor(value.colors.bg.base);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
